import { Col, Form, Input, message, Modal, Row, Select } from "antd";
import { Rule } from "antd/lib/form";
import React, { useImperativeHandle, useState } from "react";
import { ModalStatus } from "types/modal";
import { Document } from "types/document";
import { documentApi } from "api/document.api";
import { projectApi } from "api/project.api";
import { staffApi } from "api/staff.api";
import { $url } from "utils/url";
import { Project } from "types/project";
import { Staff } from "types/staff";
import { UploadFile } from "antd/lib";
import { StaffSelector } from "./StaffSelector";
import { SingleFileUpload } from "components/Upload/SingleFileUpload";
import { checkRole } from "utils/auth";
import { userStore } from "store/userStore";
import { downloadDocumentPath } from "views/Document/DocumentPage";

const rules: Rule[] = [{ required: true }];

export interface DocumentModal {
  handleCreate: () => void;
  handleUpdate: (document: Document) => void;
}
interface DocumentModalProps {
  onClose: () => void;
  onSubmitOk: () => void;
}

interface DocumentForm extends Document {
  projectId: number;
  staffIds: number[];
}

export const DocumentModal = React.forwardRef(({ onClose, onSubmitOk }: DocumentModalProps, ref) => {
  const [form] = Form.useForm<DocumentForm>();
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [status, setStatus] = useState<ModalStatus>("create");
  const [projects, setProjects] = useState<Project[]>([]);
  const [staffs, setStaffs] = useState<Staff[]>([]);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [selectedDocument, setSelectedDocument] = useState<Document>();

  const isUpdate = status === "update";

  const fetchProject = async () => {
    const res = await projectApi.findAll({ page: 1, limit: 0 });
    setProjects(res.data.projects);
  };

  const fetchStaffByProject = async (projectId: number) => {
    if (!projectId) {
      setStaffs([]);
      return;
    }
    setLoadingStaff(true);
    try {
      const res = await staffApi.findAll({ page: 1, limit: 50, projectId });
      setStaffs(res.data.staffs || []);
    } finally {
      setLoadingStaff(false);
    }
  };

  useImperativeHandle<any, DocumentModal>(
    ref,
    () => ({
      handleCreate() {
        form.resetFields();
        setVisible(true);
        setStatus("create");
        setSelectedDocument(undefined);
        setStaffs([]);
        setFileList([]);
        fetchProject();
      },
      handleUpdate(document: Document) {
        const projectId = document.project?.id;
        const staffIds = document.staffs?.map((s) => s.id) || [];
        form.setFieldsValue({
          name: document.name,
          projectId,
          filePath: document.filePath,
          staffIds,
        });
        if (document.filePath) {
          setFileList([
            {
              uid: document.filePath,
              name: document.fileName || document.filePath,
              status: "done",
              url: $url(document.filePath),
            },
          ]);
        } else {
          setFileList([]);
        }
        setSelectedDocument(document);
        setVisible(true);
        setStatus("update");
        fetchProject();
        if (projectId) {
          fetchStaffByProject(projectId);
        }
      },
    }),
    [],
  );

  const handleProjectChange = (projectId: number) => {
    form.setFieldsValue({ staffIds: [] });
    fetchStaffByProject(projectId);
  };

  const submitForm = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      // getFieldsValue(true) để lấy cả fileName (không gắn với Form.Item)
      const values = form.getFieldsValue(true);

      if (status === "create") {
        await documentApi.create({
          document: {
            name: values.name,
            filePath: values.filePath,
            fileName: values.fileName,
          },
          projectId: values.projectId,
          staffIds: values.staffIds || [],
        });
        message.success("Tạo thành công!");
      } else {
        await documentApi.update(selectedDocument?.id || 0, {
          staffIds: values.staffIds || [],
        });
        message.success("Cập nhật thành công!");
      }

      onSubmitOk();
      handleClose();
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose?.();
    setVisible(false);
    setFileList([]);
    setStaffs([]);
    setSelectedDocument(undefined);
  };

  const canDownloadDocument = checkRole(downloadDocumentPath, userStore.info.role?.permissions);

  return (
    <Modal
      onCancel={() => {
        handleClose();
      }}
      open={visible}
      title={isUpdate ? "Cập nhật tài liệu" : "Tạo tài liệu"}
      style={{ top: 20 }}
      width={700}
      confirmLoading={loading}
      onOk={submitForm}
    >
      <Form layout="vertical" form={form}>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Tên tài liệu" name="name" rules={rules}>
              <Input placeholder="" disabled={isUpdate} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label="Dự án" name="projectId" rules={rules}>
              <Select style={{ width: "100%" }} disabled={isUpdate} onChange={handleProjectChange}>
                {projects?.map((project) => (
                  <Select.Option key={project.id} value={project.id}>
                    {project.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item label="Nhân viên được xem" name="staffIds" rules={rules}>
              <StaffSelector
                mode="multiple"
                staffs={staffs}
                loading={loadingStaff}
                placeholder="Chọn nhân viên"
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item shouldUpdate={true}>
          {() => {
            return (
              <Form.Item label="Tài liệu" name="filePath" rules={rules}>
                <SingleFileUpload
                  multiple={false}
                  fileList={fileList}
                  readOnly={isUpdate}
                  onUploadOk={(file: any) => {
                    form.setFieldsValue({
                      filePath: file?.path,
                      fileName: file?.originalname,
                    });
                    setFileList([
                      {
                        uid: file.path,
                        name: file.originalname,
                        status: "done",
                        url: $url(file.path),
                      },
                    ]);
                  }}
                  onDelete={() => {}}
                  uploadUrl={import.meta.env.VITE_API_URL + "/v1/admin/documents/upload"}
                  acceptType=".doc,.docx,.xls,.xlsx,.json"
                  allowDownload={canDownloadDocument}
                />
              </Form.Item>
            );
          }}
        </Form.Item>
      </Form>
    </Modal>
  );
});
